import React, { useEffect, useState } from 'react';
import { Route } from 'react-router';
import {
    useAntiFraudQuery,
    useBorrowerPositionByLoanApplicationIdQuery,
    BorrowerPosition,
} from 'generated/graphql';
import useNavigation from 'hooks/navigation';
import Loading from 'components/loading';
import { BizonScreenWrapper, BizonCheckoutWrapper, BlankWrapper } from 'components/commons';
import AntiFraud from './context/antifraud-context';
import { useAntiFraudHelper, useApplicantHandler } from './hooks';
import LivenessCheckout from './liveness-checkout';
import LivenessIdentity from './liveness-identity';
import PartnerNotFound from './partner-not-found';
import InvalidPartner from './invalid-partner';
import SendLinkPartner from './send-link-partner';
import SuccessfulSubmission from './successful-submission';
import { getLoanApplicationParams } from 'utils/utils';
import { AntifraudSteps, useCompleteAntifraudStep } from 'hooks/complete-antifraud-step';
import WorthyCreditQuestionnaire from './worthy-credit';

type NextScreenResolverType = {
    name: string,
    supports(): boolean,
    resolve(): Promise<void>,
};

const antiFraudPath = "/:loanApplicationId/identificacao";

const AntiFraudContainer: React.FC = () => {
    const [applicantLoading, setApplicantLoading] = useState(false);
    const loanApplicationId = getLoanApplicationParams();

    const {
        data,
        loading: antifraudLoading,
        refetch,
    } = useAntiFraudQuery({ variables: { loanApplicationId } });

    const antifraudSteps: AntifraudSteps = ["createCompanyInfo", "buildChecklist", "continueWorkflow"]
    const { handleCompleteAntiFraudStep } = useCompleteAntifraudStep({antifraudSteps});

    const {
        data: borrowerData,
        loading: borrowerPositionLoading,
    } = useBorrowerPositionByLoanApplicationIdQuery({ variables: { loanApplicationId } });

    const setAntiFraudApplicant = useApplicantHandler();

    const {
        livenessIsDraft,
        hasApplicant,
        borrowerIsPartner,
        borrowerIsApplicant,
        hasWorthyCreditToAnswer,
    } = useAntiFraudHelper(data?.antiFraud, borrowerData?.borrowerPositionByLoanApplicationId);

    const {
        goToPartnerNotIdentified,
        goToShareAntiFraudPartner,
        goToLivenessStep,
        goToCreditWorthyStep,
        goToDocumentCheckout,
        goToError,
    } = useNavigation();

    useEffect(() => {
        if (antifraudLoading === false && data?.antiFraud?.isCompleted) {
            goToDocumentCheckout();
        }
    }, [antifraudLoading]);


    const resolveNextScreen = async () => {
        if (!data?.antiFraud || !borrowerData?.borrowerPositionByLoanApplicationId) {
            return;
        }

        const resolvers: NextScreenResolverType[] = [
            {
                name: 'partner_not_identified',
                supports: () => !hasApplicant() && !borrowerIsPartner(),
                resolve: async () => borrowerData?.borrowerPositionByLoanApplicationId === BorrowerPosition.NotPartner ? goToShareAntiFraudPartner() : goToPartnerNotIdentified(),
            },
            {
                name: 'liveness_step',
                supports: () => livenessIsDraft(),
                resolve: async () => {
                    if (!hasApplicant()) {
                        setApplicantLoading(true);
                        await setAntiFraudApplicant({
                            variables: {
                                applicantInput: {
                                    loanApplicationId,
                                }
                            }
                        });
                    }
                    goToLivenessStep();
                },
            },
            {
                name: 'worthy_credit_step',
                supports: () => hasWorthyCreditToAnswer(),
                resolve: async () => goToCreditWorthyStep(),
            },
        ];


        const resolver = resolvers.find(res => res.supports());
        if (resolver) {
            try {
                await resolver.resolve();
            } catch (error) {
                setApplicantLoading(false);
                goToError();
            }
        }
    };

    if (antifraudLoading || borrowerPositionLoading) {
        return (
            <Loading />
        )
    }

    return (
        <AntiFraud
            antiFraud={data?.antiFraud}
            borrowerPosition={borrowerData?.borrowerPositionByLoanApplicationId}
            refetch={refetch}
        >
            <Route
                exact
                path={antiFraudPath}
                render={() => (
                    <BizonCheckoutWrapper>
                        <LivenessCheckout
                            nextScreenButtonLoading={applicantLoading}
                            nextScreenButtonDisabled={hasApplicant() && !borrowerIsApplicant()}
                            nextScreen={resolveNextScreen}
                        />
                    </BizonCheckoutWrapper>
                )}
            />

            {!hasApplicant() && !borrowerIsPartner() && (
                <>
                    <Route
                        path={`${antiFraudPath}/socio-nao-identificado`}
                        render={() => (
                            <BizonScreenWrapper>
                                <PartnerNotFound />
                            </BizonScreenWrapper>
                        )}
                    />

                    <Route
                        path={`${antiFraudPath}/socio-invalido`}
                        render={() => (
                            <BizonScreenWrapper>
                                <InvalidPartner />
                            </BizonScreenWrapper>
                        )}
                    />

                    <Route
                        path={`${antiFraudPath}/compartilhe-com-socio`}
                        render={() => (
                            <BizonScreenWrapper>
                                <SendLinkPartner
                                    title= {borrowerData?.borrowerPositionByLoanApplicationId === BorrowerPosition.NotPartner
                                        ? 'ops! identificamos pelo CPF que você não é um dos sócios'
                                        : undefined
                                    }
                                />
                            </BizonScreenWrapper>
                        )}
                    />

                    <Route
                        path={`${antiFraudPath}/enviado-com-sucesso`}
                        render={() => (
                            <BizonScreenWrapper>
                                <SuccessfulSubmission />
                            </BizonScreenWrapper>
                        )}
                    />
                </>
            )}

            {livenessIsDraft() && (
                <Route
                    path={`${antiFraudPath}/prova-de-vida`}
                    render={() => (
                        <BlankWrapper>
                            <LivenessIdentity
                                onComplete={() => hasWorthyCreditToAnswer() ? goToCreditWorthyStep() : handleCompleteAntiFraudStep()}
                            />
                        </BlankWrapper>
                    )}
                />
            )}

            {hasWorthyCreditToAnswer() && (
                <Route
                    path={`${antiFraudPath}/questionario`}
                    render={() => (
                        <BizonScreenWrapper>
                            <WorthyCreditQuestionnaire onComplete={handleCompleteAntiFraudStep} />
                        </BizonScreenWrapper>
                    )}
                />
            )}
        </AntiFraud>
    );
};

export default AntiFraudContainer;
